import { roundNumericString } from "@/utils/helper";
import { toSavePayloadNutrientId } from "@/utils/petFoodNutrientId";
import NutrientService from "@/services/nutritionService";

export interface FolateFoodDetails {
    foodFolate?: number | string | null;
    unitOfMeasure?: string | null;
}

export const FOOD_FOLATE_DEFAULT_UNIT = "mcg";

export const FOOD_FOLATE_UNIT_OPTIONS = [
    { label: "mcg", value: "mcg", name: "mcg" },
] as const;

export function isFolateNutrient(name?: string | null): boolean {
    return String(name ?? "").replace(" *", "").trim().toLowerCase() === "folate";
}

export function getPersistedFolateNutrientId(
    nutrients: Array<{ id?: string; nutrient?: string }> | undefined | null
): string | number | undefined {
    const folate = (nutrients || []).find((n) => isFolateNutrient(n.nutrient));
    return toSavePayloadNutrientId(folate?.id);
}

/**
 * Secondary line for Folate in "Unrounded Per 100g" column, e.g. "(360 mcg Food Folate)".
 */
export function formatFoodFolateSubLine(
    foodFolate?: number | string | null,
    unitOfMeasure?: string | null
): string {
    const value = roundNumericString(foodFolate);
    if (!value) return "";

    const unit = String(unitOfMeasure ?? "").trim();
    const parts = [value, unit, "Food Folate"].filter(Boolean);
    return `(${parts.join(" ")})`;
}

export function parseFoodFolateForPayload(value?: string): number | undefined {
    const rounded = roundNumericString(value);
    if (!rounded) return undefined;
    const num = parseFloat(rounded);
    return Number.isFinite(num) ? num : undefined;
}

const folateDetailsCache = new Map<string, FolateFoodDetails>();
const folateDetailsInflight = new Map<string, Promise<FolateFoodDetails | null>>();

/**
 * Fetch folate food details once per nutrient id (module-level cache + in-flight dedupe).
 */
export async function fetchFolateNutrientDetails(
    nutrientId: string | number
): Promise<FolateFoodDetails | null> {
    const cacheKey = String(nutrientId);
    const cached = folateDetailsCache.get(cacheKey);
    if (cached) return cached;

    const inflight = folateDetailsInflight.get(cacheKey);
    if (inflight) return inflight;

    const request = (async (): Promise<FolateFoodDetails | null> => {
        try {
            const response = await NutrientService.getNutrient(nutrientId);
            if (!isFolateNutrient(response?.nutrient)) return null;
            if (response.foodFolate == null) return null;

            const details: FolateFoodDetails = {
                foodFolate: response.foodFolate,
                unitOfMeasure: response.unitOfMeasure,
            };
            folateDetailsCache.set(cacheKey, details);
            return details;
        } finally {
            folateDetailsInflight.delete(cacheKey);
        }
    })();

    folateDetailsInflight.set(cacheKey, request);
    return request;
}

export function invalidateFolateNutrientCache(nutrientId?: string | number): void {
    if (nutrientId == null) {
        folateDetailsCache.clear();
        folateDetailsInflight.clear();
        return;
    }
    const cacheKey = String(nutrientId);
    folateDetailsCache.delete(cacheKey);
    folateDetailsInflight.delete(cacheKey);
}
